/**
 * Posting Bot — de vier blokken van de huidige pagina, in dezelfde volgorde:
 *   1. Blocked posting terms
 *   2. Auto-Posting per Model   (modellen als RIJEN onder elkaar, geen kaarten)
 *   3. Live Bot Monitor         (incl. de 5-schermen live browser)
 *   4. Scheduled Posts Queue
 *
 * Endpoints, exact zoals de bundle ze aanroept:
 *   /api/admin/posting/configs · /api/admin/models/personas?split=true
 *   /api/admin/posting/master-status · /api/admin/posting/stats
 *   /api/admin/posting/vault-cleanup-stats · /api/admin/posting/scheduled
 *   /api/admin/posting/live-log · /api/admin/posting/live-batch (5 schermen)
 *   /api/admin/dashboard/settings/posting_blocked_terms
 *
 * Mutaties (Start/Stop/Start All/Schedule All) lopen via dezelfde PATCH op
 * /api/admin/posting/config/{id} en POST /api/admin/posting/bulk-start als de
 * huidige bundle. De 24-uursregel wordt hier in de UI gespiegeld; de backend
 * weigert een te korte aanloop hoe dan ook met 422.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Avatar, Empty, ErrorNote, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { useApi, usePoll } from '../lib/useApi'
import { api } from '../lib/api'
import { useActions, mut } from '../lib/actions'
import { ManageDialog } from '../components/ManageDialog'
import { VaultTeVerwijderen } from '../components/VaultTeVerwijderen'
import { dt, dtSec, num } from '../lib/format'
import type { Persona } from './Models'
import { LiveBrowser } from '../components/LiveBrowser'
import { ScheduleAllDialog } from '../components/ScheduleAllDialog'
import { StartAllDialog } from '../components/StartAllDialog'
import { Activiteit } from '../components/Activiteit'
import { useNavigate } from 'react-router-dom'

interface Config {
  id: number
  model_persona_id: number
  interval_minutes: number
  active: boolean
  repeat_mode: boolean
  audience_order: string[] | null
  vault_delete_delay_minutes: number
  start_from: string | null
  auto_stop_at: string | null
  preferred_platform: string | null
  total_posts: number
  current_post_index: number
  successful_post_count: number
  posts_today?: number
  /** 25-09: alles in post_history voor dit model, los van de ronde.
   *  `successful_post_count` is alleen de lopende ronde ("this run"). */
  posted_ever_count?: number
  posted_ever_since?: string | null
  /** Waarom dit model aan staat en toch niets oppakt; leeg als er niets is. */
  idle_reason?: string | null
  pending_post_count: number
  content_remaining_count: number
  /** "cycle 3 · item 3 of 76" — waar dit model in zijn eigen ronde staat. */
  cycle?: number
  cycle_item?: number | null
  cycle_label?: string
  /** Waaruit de wachtrij bestaat. Zelfde telling als in de Manage-dialoog. */
  queue_text_count?: number
  queue_photo_count?: number
  queue_video_count?: number
}
/** Wat /api/admin/posting/readiness teruggeeft. */
interface Gereedheid {
  ready: number
  total: number
  models: {
    model_persona_id: number
    slug: string
    platform: string
    ready: boolean
    reasons: string[]
    warnings: string[]
  }[]
}
interface Scheduled {
  id: number; model_persona_id: number; caption: string; platform: string
  status: string; scheduled_at: string; published_at: string | null
  expires_at?: string | null
  media_urls: string[] | null
  /** 'fc:<id>' met daarachter de stand: |http, |ok, |done of |onbevestigd. */
  platform_post_id?: string | null
  error_message?: string | null
}
interface Page<T> { data: T[]; total: number }

/** Eén model in de voortgangsroute. Tijden zijn naïef UTC uit de database. */
interface VoortgangModel {
  model_persona_id: number
  slug: string
  name: string
  active: boolean
  start_from: string | null
  post_until: string | null
  interval_minutes: number | null
  cycle: number
  item: number | null
  of_total: number | null
  /** Laatste post die de fan al ziet. */
  live_through: string | null
  /** Voor de balk per model. */
  transferred: number
  /** 16-09: wat er vandaag echt is doorgezet; die teller staat nooit op nul
   *  door een reset of een nieuwe Start at. */
  today?: number
  live: number
  /** Het aantal slots van Start at tot Post until — de opdracht. */
  total: number
  /** Heeft dit model zowel een Start at als een Post until? Alleen die
   *  modellen tellen mee in het voortgangsblok — ook als ze uit staan. */
  heeft_venster?: boolean
  /** 'slots' of 'queue': waar `total` vandaan komt. Zonder Post until valt
   *  er niets vast te rekenen en telt de route de wachtrij. */
  total_source?: 'slots' | 'queue'
  slots_until_stop?: number | null
  in_queue?: number
  /** Laatste post die aan het platform is overgedragen (mag in de toekomst liggen). */
  scheduled_through: string | null
  /** Eerstvolgende rij die de bot nog moet aanbieden. */
  next_handover: string | null
  errors: number
}
interface Voortgang {
  models: VoortgangModel[]
  /** 16-09: totaal doorgezet vandaag, over alle modellen. */
  today?: number
  active_models: number
  /** 25-09: aan per platform, over dezelfde modellen als de tabel. Een config
   *  die aan staat bij een persona die uit of verborgen is, staat apart. */
  platforms?: {
    fancentro: { on: number; total: number }
    f2f: { on: number; total: number }
    on_but_skipped: string[]
  }
  scheduled_through_min: string | null
  post_until_max: string | null
  cycle: number
  errors: number
  /** Overgedragen aan het platform (staat in Scheduled of is al live). */
  transferred: number
  /** Daarvan al zichtbaar voor de fans. */
  live: number
  /** Wat er in totaal hoort te komen tot Post until: de som van de slots van
   *  de actieve modellen. */
  total: number
  remaining: number
  /** Werkelijke overdrachten in het laatste uur. */
  per_hour: number
  /** 27-09: posts die in het laatste uur live gingen voor de fans. */
  live_per_hour?: number
  hours_left: number | null
  interval_minutes: number
  per_model_per_day: number
  per_day: number
  /** Modellen met beide tijden; de noemer van het blok telt over deze. */
  models_in_window: number
  /** Daarvan uitgeschakeld. Dat is wat "Hervatten" weer aan zou zetten. */
  paused_models: number
  /** Alles met een venster ligt stil: geen tempo tonen, maar "gepauzeerd". */
  gepauzeerd: boolean
}
/** Wat /afbreek-stand teruggeeft: de laatste uitslag en of er iets stilstaat. */
interface AfbreekStand {
  last_result: {
    line: string
    /** Wanneer de afbreking was (ISO). */
    at?: string
    lanes_stopped: number
    rows_still_queued: number
    rows_awaiting_platform_check: number
    already_handed_over: number
    locks_still_held: string[]
  } | null
  barrier: { scope: string; model_persona_id: number | null; by: string } | null
  /** Er ligt een verzoek dat de worker nog niet heeft opgepakt. */
  pending: boolean
}

/** Wat /hervatten teruggeeft, zowel bij de proef als bij de echte klik. */

/** "2 d 15 h" — hoe lang het nog duurt, in woorden die je kunt plannen. */
function duurtekst(uren: number | null): string {
  if (uren === null || !Number.isFinite(uren)) return 'unknown (no rate)'
  const d = Math.floor(uren / 24)
  const u = Math.round(uren % 24)
  return d ? `${d} d ${u} h` : `${u} h`
}

/** Duizendtallen met een komma: 14,759. Het scherm is Engels (Jordy, 09-09),
 *  dus de getalopmaak volgt die taal en niet de serverlocale. */
function nl(n: number): string {
  return n.toLocaleString('en-US')
}

/**
 * De balk van 0 naar 100%, met twee kleuren.
 *
 * Groen is wat de fans al zien; blauw is overgedragen en wacht in de
 * Scheduled-tab van FanCentro. Dat onderscheid staat er met opzet in: onze
 * status `published` betekent "aangenomen door het platform", niet "zichtbaar",
 * en de bot draagt tot een maand vooruit over. Eén kleur zou suggereren dat er
 * al duizenden posts live staan.
 */
function TweekleurBalk({ live, overgedragen, totaal, hoog = 10 }: {
  live: number; overgedragen: number; totaal: number; hoog?: number
}) {
  const pct = (n: number) => (totaal > 0 ? Math.min(100, (n / totaal) * 100) : 0)
  return (
    <div style={{ position: 'relative', height: hoog, borderRadius: hoog / 2,
                  background: 'var(--mut)', overflow: 'hidden' }}>
      <div title="handed over, waiting in Scheduled"
           style={{ position: 'absolute', inset: 0, width: `${pct(overgedragen)}%`,
                    background: 'var(--acc)' }} />
      <div title="already live for fans"
           style={{ position: 'absolute', inset: 0, width: `${pct(live)}%`,
                    background: 'var(--ok)' }} />
    </div>
  )
}

/** Is deze afbreking van de laatste 24 uur? Zonder tijdstip: tonen. */
function afbreekRecent(at: string | undefined): boolean {
  if (!at) return true
  const t = new Date(at.includes('Z') || at.includes('+') ? at : at + 'Z').getTime()
  return Number.isNaN(t) || Date.now() - t < 24 * 3600 * 1000
}

/** "11-09 14:20" uit een naïeve UTC-tijdstempel, in Amsterdamse tijd. */
function klok(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.includes('Z') || iso.includes('+') ? iso : iso + 'Z')
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).replace(',', ' ·').replace(' ·', '')
}

/**
 * Het blok "Voortgang tot Post until", boven de modellenlijst.
 *
 * Waarom het een eigen blok is (Jordy, 09-09)
 * -------------------------------------------
 * De totaalbalk stond ingeklemd tussen de knoppenrij en de tabel, en de balk
 * per model stond in de kolom Progress. Daardoor las je twee keer hetzelfde in
 * twee vormen en geen van beide goed: de totaalbalk was smal en de balkjes in
 * de tabel waren te klein om iets af te lezen. Nu staat de opdracht als geheel
 * bovenaan in één brede balk, en de modellen eronder als compacte regels die
 * je kunt inklappen als je ze niet nodig hebt.
 *
 * De noemer is het aantal SLOTS van Start at tot Post until bij het ingestelde
 * interval — niet het aantal foto's in de map en niet het aantal rijen in de
 * wachtrij. Een model met 76 foto's en 938 slots begint na foto 76 weer bij 1;
 * de opdracht is 938 posts. De oude noemer telde wachtrij-rijen en groeide dus
 * mee met de teller, waardoor de balk bleef hangen op hetzelfde percentage.
 */
function VoortgangBlok({ v, afbreekStand }: {
  v: Voortgang
  /** De uitslag van de laatste afbreking, als die er is. */
  afbreekStand: AfbreekStand | null
}) {
  const [open, setOpen] = useState(false)
  // Het blok telt de modellen met een Start at én een Post until, ongeacht of
  // ze aan staan (Jordy, 09-09). Hing dit aan `active`, dan verdween de hele
  // opdracht uit beeld zodra hij Stop All deed — en juist dan wil je zien waar
  // je gebleven was. Een uitgezet model is niet klaar, hij ligt stil.
  const modellen = (v.models ?? []).filter((m) => m.heeft_venster)
  const pct = v.total > 0 ? (v.transferred / v.total) * 100 : 0
  const klaarOm = v.hours_left !== null
    ? new Date(Date.now() + v.hours_left * 3600_000).toISOString()
    : null
  // Staat er ook maar één model zonder Post until bij, dan is de noemer voor
  // dat deel de wachtrij en niet de opdracht. Dat hoort erbij te staan.
  const geschat = modellen.filter((m) => m.total_source === 'queue')

  return (
    <Panel
      title="Progress to Post until"
      desc="Posts created on the platform, out of every slot between Planning starts at and Post until"
      right={
        <>
          {/* Start, Pause en Stop staan sinds 14-09 in de balk van "Auto-Posting
              per Model" en op elke modelregel (Jordy: "3 knoppen en niet
              meer"). Dit blok toont alleen de voortgang. */}
          <button className="btn ghost sm" type="button"
                  onClick={() => setOpen((x) => !x)}>
            {open ? 'Hide models' : `Show models (${modellen.length})`}
          </button>
        </>
      }
    >
      {modellen.length === 0 ? (
        <Empty>No model has both a Planning starts at and a Post until</Empty>
      ) : (
        <>
          <div className="between" style={{ marginBottom: 6 }}>
            <b style={{ fontSize: 22, lineHeight: 1 }}>
              {/* 15-09 (Jordy: "lijkt niet te bewegen"). De noemer is elk slot
                  van elk model tot Post until, duizenden posts; op hele
                  procenten bewoog het getal pas na ruim honderd posts. */}
              {pct.toFixed(1)}%
            </b>
            <span className="mut2" style={{ fontSize: 11 }}>
              <span style={{ color: 'var(--ok)' }}>■</span> live for fans ·{' '}
              <span style={{ color: 'var(--acc)' }}>■</span> handed over,
              waiting in Scheduled
            </span>
          </div>
          {/* Eén brede balk, hoog genoeg om de twee kleuren te onderscheiden. */}
          <TweekleurBalk live={v.live} overgedragen={v.transferred}
                         totaal={v.total} hoog={16} />
          <div style={{ fontSize: 13, marginTop: 8 }}>
            {`${nl(v.transferred)} of ${nl(v.total)} posts`}
            {` · ${nl(v.remaining)} to go`}
            {` · ${nl(v.live)} live for fans in this run`}
            {typeof v.today === 'number' ? ` · ${nl(v.today)} pushed today` : ''}
          </div>
          <div className="mut2" style={{ fontSize: 11, marginTop: 3, lineHeight: 1.4 }}>
            Counts a post once the bot has created it on the platform. It stands
            still while models are stopped, paused or waiting on FanCentro's
            24-hour limit, and each model counts from its own Planning starts
            at: moving it starts that model's count again from the new date.
          </div>
          <div className="mut2" style={{ fontSize: 12, marginTop: 3 }}>
            {/* Tempo en resttijd alleen als er werkelijk iets loopt. Met alles
                uit was het tempo nul en de resttijd oneindig, en dan stond er
                "klaar over onbekend" alsof er iets stuk was. */}
            {v.gepauzeerd ? (
              <b style={{ color: 'var(--warn)' }}>paused</b>
            ) : (
              <>
                {/* 27-09 (Jordy): "0 per hour" las als een bot die stilligt,
                    terwijl er elk uur posts live gingen. Aanmaken en live
                    gaan staan nu apart; de resttijd alleen als er iets wordt
                    aangemaakt. */}
                {`created on FanCentro: ${v.per_hour} in the last hour`}
                {typeof v.live_per_hour === 'number'
                  ? ` · went live for fans: ${v.live_per_hour} in the last hour` : ''}
                {v.per_hour > 0
                  ? ` · done in ~${duurtekst(v.hours_left)}`
                    + (klaarOm ? ` (${klok(klaarOm)})` : '')
                  : ''}
              </>
            )}
            {` · runs until ${klok(v.post_until_max)}`}
          </div>
          <div className="mut2" style={{ fontSize: 11, marginTop: 2 }}>
            {`${modellen.length} model${modellen.length === 1 ? '' : 's'} with a window`}
            {/* 25-09 (Jordy): "16 switched on" telde alleen FanCentro, de
                tabel eronder toont ook F2F. Nu per platform, en een config
                die de planner overslaat (persona uit) apart. */}
            {v.platforms
              ? ` · ${v.platforms.fancentro.on} of ${v.platforms.fancentro.total} FanCentro on`
                + ` · ${v.platforms.f2f.on} of ${v.platforms.f2f.total} F2F on`
                + (v.platforms.on_but_skipped.length
                  ? ` · also on but skipped (persona off or hidden): `
                    + v.platforms.on_but_skipped.join(', ')
                  : '')
              : `, ${v.active_models} switched on`}
            {` · interval ${v.interval_minutes} min`}
            {` · ${v.per_model_per_day} per model per day`}
            {` · ${nl(v.per_day)} per day combined`}
            {` · round ${v.cycle}`}
            {v.errors ? ` · errors ${nl(v.errors)}` : ''}
          </div>
          {/* De uitslag van de laatste afbreking. Hij blijft staan tot er
              een nieuwe is: wie afbreekt wil later nog kunnen nakijken wat er
              precies is gebeurd, en een melding die na vijf seconden weg is
              beantwoordt die vraag niet. */}
          {/* 27-09 (Jordy): een afbreking van dagen geleden stond er nog
              alsof hij net gebeurde. Nu met datum, en na 24 uur weg. */}
          {afbreekStand?.last_result && afbreekRecent(afbreekStand.last_result.at) ? (
            <div style={{ fontSize: 11, marginTop: 6,
                          color: 'var(--warn)' }}>
              {afbreekStand.last_result.at
                ? `${klok(afbreekStand.last_result.at)} · ` : ''}
              {afbreekStand.last_result.line}
              {afbreekStand.last_result.locks_still_held?.length
                ? ` · LOCKS STILL HELD: ${
                    afbreekStand.last_result.locks_still_held.join(', ')}`
                : ''}
            </div>
          ) : null}
          {afbreekStand?.barrier ? (
            <div style={{ fontSize: 11, marginTop: 3, color: 'var(--warn)' }}>
              {afbreekStand.barrier.scope === 'all'
                ? 'Handover aborted — nothing restarts until you press Start'
                : 'Handover aborted for one model — it restarts when you '
                  + 'press Start'}
            </div>
          ) : null}
          {afbreekStand?.pending ? (
            <div style={{ fontSize: 11, marginTop: 3, color: 'var(--warn)' }}>
              Abort requested — waiting for the worker to pick it up
            </div>
          ) : null}
          {geschat.length ? (
            <div className="mut2" style={{ fontSize: 11, marginTop: 2 }}>
              {`${geschat.length} model${geschat.length === 1 ? '' : 's'} `}
              {'without a Post until; for those the queue counts instead of a '}
              {'fixed number of slots'}
            </div>
          ) : null}
        </>
      )}

      {/* De regels per model: dezelfde getallen, maar dan uitgesplitst. Ze
          staan ingeklapt omdat je ze meestal niet nodig hebt — de totaalbalk
          zegt of het goed gaat, deze lijst zegt wie achterloopt. */}
      {open && modellen.length ? (
        <div style={{ marginTop: 14, display: 'grid', gap: 6 }}>
          {modellen.map((m) => {
            const p = m.total > 0 ? (m.transferred / m.total) * 100 : 0
            return (
              <div key={m.model_persona_id} style={{ minWidth: 0 }}>
                <div className="between" style={{ fontSize: 12, marginBottom: 2 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap' }}>
                    <b>{m.name}</b>
                    {!m.active ? (
                      <Tag kind="warn">paused</Tag>
                    ) : null}
                    <span className="mut2">
                      {m.item && m.of_total
                        ? ` · item ${m.item} of ${m.of_total}` : ' · no items'}
                      {` · cycle ${m.cycle}`}
                      {m.errors ? ` · ${m.errors} failed` : ''}
                    </span>
                  </span>
                  <span className="mut2" style={{ whiteSpace: 'nowrap',
                                                  display: 'flex', gap: 6,
                                                  alignItems: 'center' }}>
                    {`${nl(m.transferred)} / ${nl(m.total)} · ${Math.round(p)}%`}
                  </span>
                </div>
                <TweekleurBalk live={m.live} overgedragen={m.transferred}
                               totaal={m.total} hoog={6} />
                <div className="mut2" style={{ fontSize: 11, marginTop: 2 }}>
                  {`last scheduled ${klok(m.scheduled_through)}`}
                  {` · next handover ${klok(m.next_handover)}`}
                  {` · runs until ${klok(m.post_until)}`}
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
    </Panel>
  )
}

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]
const STATUS_KIND: Record<string, 'ok' | 'warn' | 'acc' | 'bad' | 'mut'> = {
  published: 'ok', scheduled: 'warn', publishing: 'acc',
  failed: 'bad', cancelled: 'mut', dry_run: 'mut',
  'on schedule': 'acc', live: 'ok',
  // Geel, niet rood: er is niets misgegaan, er is alleen iets niet bevestigd.
  'on platform, unconfirmed': 'warn',
}

/**
 * Wat de rij écht laat zien, in de woorden van het platform.
 *
 * `published` in onze database betekent "overgedragen aan het platform", niet
 * "zichtbaar voor de fan". De bot draagt ver vooruit over — gemeten op oud:
 * 5.656 FC-posts staan gemiddeld 172 uur vooruit gepland — en FanCentro of F2F
 * zet hem live op `scheduled_at`. Eén woord voor allebei las alsof er 's nachts
 * honderd posts tegelijk uit waren gegaan.
 *
 * Daarom twee woorden:
 *   on schedule — overgedragen, staat in de Scheduled-tab, gaat nog live
 *   live        — dat moment is verstreken, het platform heeft hem gepubliceerd
 *
 * Alle andere statussen blijven zoals ze zijn; die gaan over ons, niet over het
 * platform.
 */
function toonStatus(s: { status: string; scheduled_at: string
                        platform_post_id?: string | null }): string {
  if (s.status !== 'published') return s.status
  // 09-09: een post die FanCentro aannam maar 24 uur na zijn eigen live-tijd
  // nog steeds in moderatie hield. De controle is dan gestopt. Hij staat wel
  // degelijk op het platform, dus dit is geen fout en geen mislukte post — we
  // hebben alleen niet kunnen zien dát hij open ging. De reden staat in
  // error_message. Zie src/services/posting/herverificatie.py.
  if ((s.platform_post_id || '').endsWith('|onbevestigd')) {
    return 'on platform, unconfirmed'
  }
  const gaatLiveOp = new Date(s.scheduled_at).getTime()
  if (!Number.isFinite(gaatLiveOp)) return 'live'
  return gaatLiveOp > Date.now() ? 'on schedule' : 'live'
}
const QUEUE_COLS = [
  { key: 'sel', label: '' },
  { key: 'status', label: 'Status' }, { key: 'plat', label: 'Plat' },
  { key: 'model', label: 'Model' }, { key: 'caption', label: 'Caption' },
  // 15-09 (Jordy, POSTING-010): drie aparte momenten. Pushed = de exacte
  // aanmaakactie op FanCentro (`published_at`), Goes live = `scheduled_at`,
  // Expires = wanneer de opruiming hem weghaalt.
  { key: 'pushed', label: 'Pushed' }, { key: 'sched', label: 'Goes live' },
  { key: 'expires', label: 'Expires' },
  { key: 'act', label: 'Actions' },
]

export default function Posting() {
  const configs = useApi<Config[]>('/api/admin/posting/configs')
  /** Kan elk model starten? Voedt het paneel bovenaan en maakt Start grijs bij
   *  een blokkerende reden, zodat je niet klikt en pas later merkt dat er niets
   *  gebeurde. */
  const gereed = useApi<Gereedheid>('/api/admin/posting/readiness')
  /** 10-10 (Jordy): per-creator-pauze van de identiteitsbewaking, zichtbaar per model. */
  const creatorPauzes = useApi<{ items: { model_persona_id: number; reason: string; since: string | null }[] }>(
    '/api/admin/posting/creator-pauses')
  /** Waar staat elk model in zijn ronde — voedt de balken. Ververst elke 5 s
   *  zonder de rij opnieuw op te bouwen (alleen de balk krijgt nieuwe waarden). */
  const voortgang = useApi<Voortgang>('/api/admin/posting/voortgang')
  // Elke 10 s verversen (Jordy): de balk beweegt langzaam, en vaker vragen
  // levert alleen meer werk voor het webproces op zonder dat je iets ziet.
  usePoll(10000, voortgang.refetch)
  /** De laatste afbreking, en of er nog iets stilstaat door een afbreking.
   *  Ververst mee met de voortgang: hij hoort bij hetzelfde blok. */
  const afbreekStand = useApi<AfbreekStand>('/api/admin/posting/afbreek-stand')
  usePoll(10000, afbreekStand.refetch)
  const personas = useApi<Persona[]>('/api/admin/models/personas?split=true')
  const master = useApi<{
    enabled: boolean
    gates?: Record<'publish' | 'cleanup', {
      open: boolean; mode: string; describes: string; unset: boolean
      env: string; error: string
    }>
    /** Hoeveel posts er nog in het rooster staan te wachten. Permanent in
     *  beeld sinds 11-09: zonder dit getal is niet te zien dat er nog werk
     *  klaarstaat terwijl alles "uit" lijkt. */
    in_rooster?: number
    vault_backlog?: VaultBacklog
  }>('/api/admin/posting/master-status')
  const vault = useApi<{ tracked_bot_vault_rows: number; remaining_due: number }>(
    '/api/admin/posting/vault-cleanup-stats')

  const navigate = useNavigate()
  const [platform, setPlatform] = useState('all')
  const [showHidden, setShowHidden] = useState(false)
  const [qStatus, setQStatus] = useState('all')
  const [qModel, setQModel] = useState('all')
  const [page, setPage] = useState(0)
  const [busy, setBusy] = useState<number | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  // false = dicht, true = via de knop Schedule All, 'fc'/'f2f'/'all' =
  // via een van de Start All-knoppen, met dat platform voorgekozen.
  const [schedOpen, setSchedOpen] = useState<false | true | 'all' | 'fc' | 'f2f'>(false)
  // 16-09 (Jordy): Start All opent een venster met de datums erin, zodat hij
  // ze kan aanpassen als dat nodig is.
  const [startAllOpen, setStartAllOpen] = useState<false | 'fancentro' | 'f2f'>(false)
  const [manage, setManage] = useState<number | null>(null)
  const [qPlatform, setQPlatform] = useState('all')
  const [qContent, setQContent] = useState('all')
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [selectAll, setSelectAll] = useState(false)
  const [terms, setTerms] = useState<string | null>(null)
  const { run, busy: acting } = useActions()

  const queue = useApi<Page<Scheduled>>(
    `/api/admin/posting/scheduled?limit=100&offset=${page * 100}`, [page])
  // Hiervoor ververste de wachtrij alleen na een actie; de status "live" en de
  // tijden bleven dan staan op het moment van laden.
  usePoll(15000, queue.refetch)
  const [queueGeladen, setQueueGeladen] = useState<Date | null>(null)
  useEffect(() => { if (queue.data) setQueueGeladen(new Date()) }, [queue.data])
  const termsQ = useApi<{ value?: string }>(
    '/api/admin/dashboard/settings/posting_blocked_terms')
  const live = useApi<{ steps?: unknown[] }>('/api/admin/posting/live-log?limit=40')
  usePoll(6000, live.refetch)

  const byId = useMemo(() => {
    const m = new Map<number, Persona>()
    for (const p of personas.data ?? []) m.set(p.id, p)
    return m
  }, [personas.data])

  /** Eén rij per model — geen kaarten.
   *
   *  Onder "Show hidden" horen ook configs te staan waarvan de PERSONA niet
   *  meer in de lijst zit (inactief of weggegooid). 08-09: `fc_emilyyvyx` stond
   *  actief in posting_configs terwijl de persona op inactief staat; die rij
   *  viel hier weg op `!!x.p` en was daardoor niet te zien én niet te stoppen.
   *  Een model dat je niet kunt zien, kun je ook niet tegenhouden. */
  const rows = useMemo(() => {
    const alle = (configs.data ?? []).map((c) => ({ c, p: byId.get(c.model_persona_id) }))
    const zonderPersona = alle.filter((x) => !x.p)
    const list = alle.filter((x) => !!x.p) as { c: Config; p: Persona }[]
    const wezen: { c: Config; p: Persona }[] = showHidden
      ? zonderPersona.map(({ c }) => ({
          c,
          // Een minimale persona zodat de rij tekent en de Stop-knop werkt.
          // De naam zegt wat er aan de hand is in plaats van leeg te blijven.
          p: {
            id: c.model_persona_id,
            slug: `persona ${c.model_persona_id}`,
            stage_name: `persona ${c.model_persona_id} — inactive/removed`,
            posting_hidden: true,
          } as Persona,
        }))
      : []
    return [...list, ...wezen]
      .filter(({ p }) => (showHidden ? true : !p.posting_hidden))
      .filter(({ p }) =>
        platform === 'all' ? true
        : platform === 'fancentro' ? !!p.fancentro_model_id
        : !!p.f2f_model_id)
      .sort((a, b) => (a.p.stage_name || a.p.slug).localeCompare(b.p.stage_name || b.p.slug))
  }, [configs.data, byId, platform, showHidden])

  /**
   * Start / Pause / Stop (Jordy, 14-09): drie knoppen, en niet meer. Dezelfde
   * drie staan in de balk voor alle modellen en op elke modelregel.
   *
   * * Start — aanzetten; een afbreek-barrière die dit model raakt gaat eraf.
   *   Na Pause loopt hij verder waar hij was, na Stop begint een nieuwe ronde.
   * * Pause — alleen uit. Het rooster blijft staan.
   * * Stop  — uit, de lopende ronde afbreken, en de posts die nog wachten weg.
   *   Wat al op FanCentro staat blijft daar.
   *
   * Elke knop vraagt eerst de getallen op (`dry_run=true`) voor de
   * bevestiging, en pas na de klik gebeurt het echt.
   */
  const verversAlles = useCallback(() => {
    configs.refetch(); voortgang.refetch(); afbreekStand.refetch()
    queue.refetch(); gereed.refetch()
  }, [configs, voortgang, afbreekStand, queue, gereed])

  const pauseAll = useCallback(async () => {
    const proef = await api.post<{ models: number; posts_blijven_staan: number }>(
      '/api/admin/posting/knoppen/pauze?dry_run=true')
    if (!proef || !proef.models) { setMsg('Nothing is running'); return }
    await run({
      label: 'Pause All',
      confirm: `Pause ${proef.models} model(s)?\n`
        + `${nl(proef.posts_blijven_staan)} posts stay in the schedule, so Start `
        + 'continues where it stopped.\n\n'
        + 'A post that is being handed over right now finishes first. What is '
        + 'already on FanCentro still goes live at its own time.',
      request: async () => {
        const uit = await api.post<{ changed?: number }>(
          '/api/admin/posting/knoppen/pauze?dry_run=false')
        setMsg(`Paused: ${uit?.changed ?? 0} model(s)`)
      },
      onDone: verversAlles,
    })
  }, [run, verversAlles])

  type StopProef = {
    stop: { configs_uit?: number; lanes_running?: number; rows_publishing?: number
            posts_onderhanden?: number; model_name?: string | null }
    clear: { to_delete: number; model_name: string | null
             stays: { published: number; publishing: number; paid_or_manual: number
                      vault_cleanup_pending: number } }
  }

  const stoppen = useCallback(async (model?: { id: number; naam: string }) => {
    const q = model ? `&model_persona_id=${model.id}` : ''
    const proef = await api.post<StopProef>(
      `/api/admin/posting/knoppen/stop?dry_run=true${q}`)
    if (!proef) { setMsg('Could not read the stop preview'); return }
    const wie = model ? model.naam : 'all models'
    const onderweg = proef.stop.rows_publishing ?? proef.stop.posts_onderhanden ?? 0
    await run({
      label: model ? `Stop ${model.naam}` : 'Stop All',
      confirm: `Stop ${wie} now?\n\n`
        + `  ${nl(onderweg)} post(s) mid-handover: aborted, held until the `
        + `platform is checked\n`
        + `  ${nl(proef.clear.to_delete)} waiting post(s) removed from the schedule\n`
        + `  ${nl(proef.clear.stays.published)} already on FanCentro: stay there\n`
        + `  ${nl(proef.clear.stays.vault_cleanup_pending)} with a photo still in `
        + `the vault: kept until the vault cleanup is done\n\n`
        + 'Photos and captions in the queue stay. Press Start to begin a new '
        + 'round. Use Pause instead if you want to continue where you are.',
      request: async () => {
        const uit = await api.post<StopProef>(
          `/api/admin/posting/knoppen/stop?dry_run=false${q}`)
        const weg = (uit?.clear as { deleted?: number } | undefined)?.deleted ?? 0
        setMsg(`Stopped ${wie}: ${nl(weg)} waiting post(s) removed`)
      },
      onDone: verversAlles,
      successText: model ? undefined
        : 'Everything stopped. Nothing will be offered until you start.',
    })
  }, [run, verversAlles])

  const startModel = useCallback(async (c: Config) => {
    setBusy(c.model_persona_id); setMsg(null)
    try {
      await api.post(
        `/api/admin/posting/knoppen/start?dry_run=false&model_persona_id=${c.model_persona_id}`)
      setMsg(`Started: model ${c.model_persona_id}`)
      verversAlles()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }, [verversAlles])

  const pauseModel = useCallback(async (c: Config) => {
    setBusy(c.model_persona_id); setMsg(null)
    try {
      await api.post(
        `/api/admin/posting/knoppen/pauze?dry_run=false&model_persona_id=${c.model_persona_id}`)
      setMsg(`Paused: model ${c.model_persona_id}`)
      verversAlles()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }, [verversAlles])

  /** Start at op nu + 24 uur en Post until op het einde van de maand, voor elk
   *  model dat uit staat (Jordy, 14-09). Dan kun je ze één voor één starten
   *  zonder per model twee datums te typen. */
  /** Alle tellers op 0 voor een nieuwe ronde (Jordy, 15-09). */
  const resetCounters = useCallback(async () => {
    const proef = await api.post<{
      queue_items: number; to_reset: number; kept_for_waiting_posts: number; models: number
    }>('/api/admin/posting/knoppen/reset-counters?dry_run=true')
    if (!proef) { setMsg('Could not read the preview'); return }
    await run({
      label: 'Reset counters',
      confirm: `Reset the counters of ${proef.models} model(s) for a new run?\n\n`
        + `${proef.to_reset} of ${proef.queue_items} queue items go back to "not planned, `
        + `not posted", so "item X of Y" and "this run" start at 0 and the next run `
        + `plans every photo again.`
        + (proef.kept_for_waiting_posts
          ? `\n\n${proef.kept_for_waiting_posts} item(s) stay reserved because their post is `
            + `still waiting in the schedule.`
          : '')
        + `\n\nNothing is switched on or off, nothing is removed from the schedule and `
        + `nothing changes on the platform.`,
      request: async () => {
        const uit = await api.post<{ changed?: number }>(
          '/api/admin/posting/knoppen/reset-counters?dry_run=false')
        setMsg(`Counters reset: ${uit?.changed ?? 0} queue item(s)`)
      },
      onDone: verversAlles,
    })
  }, [run, verversAlles])

  const startAtPlus24 = useCallback(async () => {
    const proef = await api.post<{
      start_at: string; post_until: string; models: number
      skipped_running: string[]; continued?: { name: string; start_at: string }[]
    }>('/api/admin/posting/knoppen/start-at-plus-24h?dry_run=true')
    if (!proef) { setMsg('Could not read the preview'); return }
    if (!proef.models) { setMsg('No stopped or paused model to set'); return }
    const lopend = proef.skipped_running.length
      ? `\n\nLeft alone because they are running: ${proef.skipped_running.join(', ')}`
      : ''
    // Modellen die al vooruit gepland zijn, plannen verder na hun laatste post.
    const verder = proef.continued?.length
      ? '\n\nAlready planned ahead, so they continue after their last planned post:\n'
        + proef.continued.map((c) => `${c.name}: ${c.start_at.replace('T', ' ')}`).join('\n')
      : ''
    await run({
      label: 'Planning starts +24h',
      confirm: `Set Planning starts at to ${proef.start_at.replace('T', ' ')} and Post `
        + `until to ${proef.post_until.replace('T', ' ')} for ${proef.models} `
        + `model(s)?\n\nNothing is switched on or off.${verder}${lopend}`,
      request: async () => {
        const uit = await api.post<{
          changed?: number; start_at: string; post_until: string
        }>('/api/admin/posting/knoppen/start-at-plus-24h?dry_run=false')
        setMsg(`Planning starts at ${String(uit?.start_at ?? '').replace('T', ' ')}, `
               + `Post until ${String(uit?.post_until ?? '').replace('T', ' ')} `
               + `for ${uit?.changed ?? 0} model(s)`)
      },
      onDone: verversAlles,
    })
  }, [run, verversAlles])

  const queueRows = useMemo(() => {
    return (queue.data?.data ?? []).filter((s) => {
      if (qPlatform !== 'all' && s.platform !== qPlatform) return false
      if (qStatus !== 'all' && toonStatus(s) !== qStatus) return false
      if (qModel !== 'all' && String(s.model_persona_id) !== qModel) return false
      // "Content" filtert op posts met of zonder media.
      const hasMedia = (s.media_urls ?? []).length > 0
      if (qContent === 'with' && !hasMedia) return false
      if (qContent === 'without' && hasMedia) return false
      return true
    })
  }, [queue.data, qPlatform, qStatus, qModel, qContent])

  const total = queue.data?.total ?? 0

  return (
    <>
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <div className="spacer" style={{ flex: 1 }} />
        <button className="btn primary" type="button"
                onClick={() => navigate('/posting/create')}>
          + Create Manual Post
        </button>
      </div>

      {msg ? <div className="note" style={{ marginBottom: 12 }}>{msg}</div> : null}

      {/* Hier stond een paneel "Ready to start" met alle 27 modellen eronder.
          Jordy wil dat niet: het duwde de hele pagina omlaag en herhaalde bij
          twaalf F2F-modellen dezelfde drie regels over het datumslot.
          
          De controle zelf blijft: `gereed` voedt de Start-knop per model, die
          grijs wordt met de reden in de tooltip. Dat staat waar je kijkt op het
          moment dat je wilt starten, in plaats van bovenaan de pagina. */}
      <Panel title="Blocked posting terms">
        <textarea
          className="inp"
          value={terms ?? termsQ.data?.value ?? ''}
          onChange={(e) => setTerms(e.target.value)}
          placeholder="One term/domain per line or comma-separated…"
        />
        <div className="row" style={{ marginTop: 10, justifyContent: 'space-between' }}>
          <span className="hint">
            Posts are refused before upload when the title, text or tags contain
            one of these terms.
          </span>
          <button
            className="btn sm"
            type="button"
            disabled={acting === 'Save terms'}
            onClick={() => run({
              label: 'Save terms',
              request: mut.put('/api/admin/dashboard/settings/posting_blocked_terms',
                               { value: terms ?? termsQ.data?.value ?? '' }),
              onDone: termsQ.refetch,
              successText: 'Blocked terms saved.',
            })}
          >
            Save terms
          </button>
        </div>
      </Panel>

      {/* De voortgang staat in een eigen blok boven de modellenlijst
          (Jordy, 09-09): één brede balk voor de opdracht als geheel, de
          modellen eronder en inklapbaar. */}
      {/* 10-09 (Jordy: "ook degene die gestart konden worden doen niks"). De
          bot draaide, pakte werk op, en hield elke post tegen bij de poort —
          160 keer in het log en nergens op het scherm. Een bot die te zien
          draait maar niets doet is erger dan een bot die uit staat, want je
          gaat ergens anders zoeken. */}
      {master.data?.gates ? (
        <PoortMelding gates={master.data.gates} />
      ) : null}
      {typeof master.data?.in_rooster === 'number' && master.data.in_rooster > 0 ? (
        <div style={{
          border: '1px solid var(--line)', borderRadius: 10,
          padding: '8px 12px', marginBottom: 12, fontSize: 13,
        }}>
          <b>{nl(master.data.in_rooster)} posts waiting in the schedule</b>
          <span className="mut2">
            {' '}· these go out at their scheduled time, also when every model
            is switched off. Pause keeps them for later; Stop removes them.
          </span>
        </div>
      ) : null}
      {master.data?.vault_backlog ? (
        <VaultAchterstand b={master.data.vault_backlog} />
      ) : null}
      <VaultTeVerwijderen />
      <Activiteit modellen={(voortgang.data?.models ?? []).map((m) => ({
        model_persona_id: m.model_persona_id, name: m.name }))} />

      {voortgang.data ? (
        <VoortgangBlok v={voortgang.data} afbreekStand={afbreekStand.data} />
      ) : null}

      <Panel
        title="Auto-Posting per Model"
        desc={master.data?.enabled === false
          ? 'Master switch is off — Start and Start All are blocked'
          : undefined}
        flush
        right={
          <>
            <label className="chkrow">
              <span className={showHidden ? 'chk on' : 'chk'}
                    onClick={() => setShowHidden((v) => !v)}>{showHidden ? '✓' : ''}</span>
              Show hidden
            </label>
            {/* Start / Pause / Stop voor alle modellen tegelijk (Jordy, 14-09).
                Plannen met een eigen Start at en Post until loopt via
                Schedule All…; dat was eerder ook wat de drie Start All…-knoppen
                openden. */}
            {/* 15-09 (Jordy): twee aparte knoppen. De oude Start All startte
                altijd alleen FanCentro, welke tab er ook openstond. */}
            <button className="btn primary sm" type="button"
                    disabled={acting === 'Start All FC'}
                    onClick={() => setStartAllOpen('fancentro')}>Start All FC</button>
            <button className="btn primary sm" type="button"
                    disabled={acting === 'Start All F2F'}
                    onClick={() => setStartAllOpen('f2f')}>Start All F2F</button>
            <button className="btn sm" type="button"
                    disabled={acting === 'Pause All'}
                    onClick={() => pauseAll()}>Pause All</button>
            <button className="btn danger sm" type="button"
                    disabled={acting === 'Stop All'}
                    onClick={() => stoppen()}>Stop All</button>
            <button className="btn ghost sm" type="button"
                    title="Set Planning starts at to 24 hours from now and Post until to the end of the month, for every model that is not running"
                    disabled={acting === 'Planning starts +24h'}
                    onClick={() => startAtPlus24()}>Planning starts +24h</button>
            <button className="btn sm" type="button"
                    disabled={acting === 'Reset counters'}
                    onClick={() => resetCounters()}>Reset counters</button>
            <button className="btn primary sm" type="button" onClick={() => setSchedOpen(true)}>Schedule All…</button>
            <button
              className="btn ghost sm"
              type="button"
              title="Preview timestamp-matched old FanCentro bot vault leftovers, then delete only items not referenced by current FC posts."
              disabled={acting === 'Clean FC leftovers'}
              onClick={() => run({
                label: 'Clean FC leftovers',
                confirm: 'Clean up old FC vault leftovers? Only items the bot published itself and that no current FC post still uses.',
                request: async () => {
                  const targets = (configs.data ?? [])
                    .map((c) => byId.get(c.model_persona_id))
                    .filter((p) => p && p.fancentro_model_id) as Persona[]
                  let deleted = 0
                  for (const p of targets) {
                    const r = await api.post<{ deleted?: number }>(
                      `/api/admin/posting/fc-vault-purge-history-orphans/${p.id}?dry_run=false`)
                    deleted += Number(r?.deleted ?? 0)
                  }
                  setMsg(`Old FC bot vault cleanup deleted ${deleted} unreferenced leftover item(s).`)
                },
                onDone: () => { configs.refetch(); vault.refetch() },
              })}
            >
              Clean FC leftovers
            </button>
          </>
        }
      >
        {configs.loading || personas.loading ? <Loading /> :
         configs.error ? <ErrorNote error={configs.error} /> :
         !rows.length ? <Empty>No models match this filter</Empty> : (
          <Table cols={[
            { key: 'model', label: 'Model' }, { key: 'plat', label: 'Plat' },
            { key: 'int', label: 'Interval (min)', num: true },
            { key: 'aud', label: 'Audience pattern' },
            { key: 'vault', label: 'Vault delay (min)', num: true },
            { key: 'start', label: 'Planning starts at' }, { key: 'until', label: 'Post until' },
            { key: 'rep', label: 'Repeat' },
            { key: 'prog', label: 'Progress' }, { key: 'act', label: 'Actions' },
          ]}>
            {/* zie de totaalregel hierboven de tabel */}
            {rows.map(({ c, p }) => (
              <ConfigRow
                key={c.model_persona_id}
                c={c}
                p={p}
                blokkade={(gereed.data?.models ?? [])
                  .find((g) => g.model_persona_id === c.model_persona_id)
                  ?.reasons.join(' · ')}
                creatorPauze={(creatorPauzes.data?.items ?? [])
                  .find((x) => x.model_persona_id === c.model_persona_id)}
                onResume={async () => {
                  if (!window.confirm('Lift the identity-check pause for this model? '
                    + 'The check stays on and pauses it again if the creator name does not match.')) return
                  await api.post(`/api/admin/posting/creator-pauses/${c.model_persona_id}/resume`)
                  creatorPauzes.refetch()
                }}
                busy={busy === c.model_persona_id}
                onStart={() => startModel(c)}
                onPause={() => pauseModel(c)}
                onStop={() => stoppen({ id: c.model_persona_id,
                                       naam: p.stage_name || p.slug })}
                onManage={() => setManage(c.model_persona_id)}
                // 02-10 (Jordy): na het opslaan van Post until/Start at ook de
                // Start-controle verversen, anders bleef de knop grijs tot F5.
                onSaved={() => { configs.refetch(); gereed.refetch() }}
              />
            ))}
          </Table>
        )}
      </Panel>

      <LiveBrowser
        vault={vault.data ?? null}
        liveLoading={live.loading}
        // De wachtrij-rijen dragen alleen model_persona_id, geen naam. In
        // POSTS IN PROGRESS stond daardoor overal een streepje: je zag dát er
        // posts liepen, niet van wie. De naam gaat hier mee, zodat de lijst
        // leesbaar is zonder een tweede aanroep.
        queue={((queue.data?.data ?? []).map((s) => ({
          ...s,
          model: byId.get(s.model_persona_id)?.stage_name
            || byId.get(s.model_persona_id)?.slug
            || null,
        }))) as never}
      />

      <div className="panel queuepanel">
        <div className="head">
          <div>
            <h3>Scheduled Posts Queue</h3>
            <p>Live · updated {queueGeladen ? queueGeladen.toLocaleTimeString('en-GB') : '—'} · refreshes every 15 s</p>
          </div>
        </div>
        <div className="body flush">
        {/* Vier filters zoals oud: Platform, Model, Status, Content. */}
        <div className="filters" style={{ margin: '12px 14px 6px' }}>
          <select className="select" value={qPlatform} onChange={(e) => setQPlatform(e.target.value)}>
            <option value="all">All platforms</option>
            <option value="fancentro">FanCentro</option>
            <option value="f2f">F2F</option>
          </select>
          <select className="select" value={qModel} onChange={(e) => setQModel(e.target.value)}>
            <option value="all">All models</option>
            {(personas.data ?? []).map((p) => (
              <option key={p.id} value={String(p.id)}>{p.stage_name || p.slug}</option>
            ))}
          </select>
          <select className="select" value={qStatus} onChange={(e) => setQStatus(e.target.value)}>
            <option value="all">All statuses</option>
            {['scheduled', 'on schedule', 'live', 'on platform, unconfirmed',
              'publishing', 'failed', 'cancelled', 'dry_run'].map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </select>
          <select className="select" value={qContent} onChange={(e) => setQContent(e.target.value)}>
            <option value="all">All content</option>
            <option value="with">With media</option>
            <option value="without">Without media</option>
          </select>
          <button className="btn ghost sm" type="button"
                  onClick={() => setSelectAll((v) => !v)}>
            {selectAll ? 'Clear selection' : 'Select all across pages'}
          </button>
          {picked.size || selectAll ? (
            <button
              className="btn danger sm"
              type="button"
              onClick={() => run({
                label: 'Delete selected',
                confirm: selectAll
                  ? `Delete all ${num(total)} posts matching this filter?`
                  : `Delete ${picked.size} scheduled post(s)?`,
                request: mut.post('/api/admin/posting/scheduled/bulk-delete',
                                  selectAll ? { all_matching: true }
                                            : { post_ids: [...picked] }),
                onDone: () => { setPicked(new Set()); setSelectAll(false); queue.refetch() },
              })}
            >
              Delete {selectAll ? num(total) : picked.size}
            </button>
          ) : null}
        </div>

        {queue.loading ? <Loading /> : queue.error ? <ErrorNote error={queue.error} /> :
         !queueRows.length ? <Empty>No posts match the filter</Empty> : (
          <Table cols={QUEUE_COLS}>
            {queueRows.map((s) => {
              const p = byId.get(s.model_persona_id)
              return (
                <tr key={s.id}>
                  <td>
                    <span
                      className={selectAll || picked.has(s.id) ? 'chk on' : 'chk'}
                      onClick={() => setPicked((p) => {
                        const n = new Set(p)
                        if (n.has(s.id)) n.delete(s.id); else n.add(s.id)
                        return n
                      })}
                    >
                      {selectAll || picked.has(s.id) ? '✓' : ''}
                    </span>
                  </td>
                  <td>
                    <Tag kind={STATUS_KIND[toonStatus(s)] || 'mut'}>
                      {toonStatus(s)}
                    </Tag>
                  </td>
                  <td><Plat value={s.platform} /></td>
                  <td><b>{p?.stage_name || p?.slug || s.model_persona_id}</b></td>
                  <td className="mut" style={{ maxWidth: 340 }}>{s.caption}</td>
                  <td className="mut2 mono" style={{ fontSize: 12 }}
                      title="Exact moment the post was created on the platform">
                    {s.published_at ? dtSec(s.published_at) : '—'}
                  </td>
                  <td className="mut2 mono" style={{ fontSize: 12 }}>
                    {dt(s.scheduled_at)}
                    {s.scheduled_at && new Date(s.scheduled_at).getTime() > Date.now()
                      ? <span className="mut2"> · {etaFrom(s.scheduled_at)}</span> : null}
                  </td>
                  <td className="mut2 mono" style={{ fontSize: 12 }}>
                    {s.expires_at ? dt(s.expires_at) : '—'}
                  </td>
                  <td>
                    <div className="row">
                      <button
                        className="btn ghost sm"
                        type="button"
                        /* Oud opent hier het serverzijdige overzicht van de
                           post, niet het ruwe mediabestand. */
                        onClick={() => window.open(
                          `/api/admin/posting/view/${s.id}`, '_blank')}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </Table>
        )}
        {/* Voettelling en paginering exact zoals oud. */}
        <div className="row" style={{ padding: '10px 14px', alignItems: 'center' }}>
          <span className="mut2" style={{ fontSize: 12 }}>
            Showing {total ? page * 100 + 1 : 0}–{page * 100 + queueRows.length} of{' '}
            {num(total)} total posts
          </span>
          <div className="spacer" style={{ flex: 1 }} />
          <button className="btn ghost sm" type="button" disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}>← Previous</button>
          <span className="mut2" style={{ fontSize: 12 }}>
            Page {page + 1} of {Math.max(1, Math.ceil(total / 100))}
          </span>
          <button className="btn ghost sm" type="button"
                  disabled={(page + 1) * 100 >= total}
                  onClick={() => setPage((p) => p + 1)}>Next →</button>
        </div>
        </div>
      </div>

      {manage != null ? (
        <ManageDialog
          modelId={manage}
          modelName={byId.get(manage)?.stage_name || byId.get(manage)?.slug || String(manage)}
          cycleLabel={(configs.data ?? []).find((c) => c.model_persona_id === manage)?.cycle_label}
          onClose={() => setManage(null)}
          onChanged={() => { configs.refetch(); queue.refetch() }}
        />
      ) : null}

      {startAllOpen ? (
        <StartAllDialog
          platform={startAllOpen}
          onClose={() => setStartAllOpen(false)}
          onDone={(m) => { setMsg(m); verversAlles() }}
        />
      ) : null}
      {schedOpen ? (
        <ScheduleAllDialog
          personas={personas.data ?? []}
          platformVast={schedOpen === true ? undefined : schedOpen}
          onClose={() => setSchedOpen(false)}
          onDone={(m) => { setMsg(m); configs.refetch() }}
        />
      ) : null}
    </>
  )
}


/** Loopt een audience_order samen tot "3 public, 1 follower", zoals oud. */
function audiencePattern(order: string[] | null): string {
  if (!order || !order.length) return ''
  const out: string[] = []
  let last = '', n = 0
  const flush = () => {
    if (!n) return
    const word = last === 'followers' ? (n === 1 ? 'follower' : 'followers') : last
    out.push(`${n} ${word}`)
  }
  for (const a of order) {
    if (a === last) { n += 1; continue }
    flush(); last = a; n = 1
  }
  flush()
  return out.join(', ')
}

/** De gangbare afwisselingen, als keuzelijst.
 *
 *  Een vrij tekstveld was hier foutgevoelig: "3 pubic, 1 follower" leverde
 *  stilzwijgend een ander patroon op dan bedoeld, en je zag pas bij de derde
 *  post dat het misging. De lijst dekt wat er in de praktijk wordt gebruikt;
 *  staat het gewenste patroon er niet bij, dan zetten twee tellers het samen.
 */
const AUDIENCE_KEUZES: { waarde: string; label: string }[] = [
  { waarde: '', label: 'All posts public (default)' },
  { waarde: '1 public, 1 followers', label: 'Alternating — 1 public, 1 followers' },
  { waarde: '2 public, 1 followers', label: '2 public, then 1 followers' },
  { waarde: '3 public, 1 followers', label: '3 public, then 1 followers' },
  { waarde: '4 public, 1 followers', label: '4 public, then 1 followers' },
  { waarde: '1 public, 2 followers', label: '1 public, then 2 followers' },
  { waarde: '0 public, 1 followers', label: 'Followers only' },
]

/** Het patroon uit elkaar halen in twee getallen, voor de eigen combinatie. */
function patroonDelen(tekst: string): { publiek: number; volgers: number } {
  const lijst = parsePattern(tekst) || []
  return {
    publiek: lijst.filter((a) => a === 'public').length,
    volgers: lijst.filter((a) => a === 'followers').length,
  }
}

function delenNaarPatroon(publiek: number, volgers: number): string {
  const d: string[] = []
  if (publiek > 0) d.push(`${publiek} public`)
  if (volgers > 0) d.push(`${volgers} followers`)
  return d.join(', ')
}

/** De keuze van het publiek: een lijst met de gangbare patronen, en twee
 *  tellers zodra je iets wilt wat er niet in staat.
 *
 *  Wat je kiest is een CYCLUS die zich herhaalt: "3 publiek, 1 volgers"
 *  betekent drie posts voor iedereen, dan één alleen voor volgers, en dan weer
 *  van voren af aan. Dat stond nergens en was uit een tekstveld met
 *  "3 public, 1 followers" ook niet af te leiden. */
function AudienceKeuze({ waarde, onKies }: {
  waarde: string
  onKies: (v: string) => void
}) {
  const bekend = AUDIENCE_KEUZES.some((k) => k.waarde === waarde)
  const [eigen, setEigen] = useState(!bekend && !!waarde)
  const delen = patroonDelen(waarde)

  if (eigen) {
    return (
      <div className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
        <input className="inp cell" type="number" min={0} max={20}
               style={{ width: 52 }} value={delen.publiek}
               onChange={(e) => onKies(delenNaarPatroon(
                 Math.max(0, Number(e.target.value) || 0), delen.volgers))} />
        <span className="hint">public</span>
        <input className="inp cell" type="number" min={0} max={20}
               style={{ width: 52 }} value={delen.volgers}
               onChange={(e) => onKies(delenNaarPatroon(
                 delen.publiek, Math.max(0, Number(e.target.value) || 0)))} />
        <span className="hint">followers</span>
        <button className="linkbtn" type="button" title="Back to the list"
                onClick={() => { setEigen(false); onKies('') }}>✕</button>
      </div>
    )
  }
  return (
    <select className="select cell wide" value={waarde}
            onChange={(e) => {
              if (e.target.value === '__eigen') { setEigen(true); return }
              onKies(e.target.value)
            }}>
      {AUDIENCE_KEUZES.map((k) => (
        <option key={k.label} value={k.waarde}>{k.label}</option>
      ))}
      <option value="__eigen">Custom combination…</option>
    </select>
  )
}

/** Zet "3 public, 1 follower" weer om naar de lijst die de API verwacht. */
function parsePattern(text: string): string[] | null {
  const t = text.trim()
  if (!t) return null
  const out: string[] = []
  for (const part of t.split(',')) {
    const m = part.trim().match(/^(\d+)\s+(\w+)/)
    if (!m) continue
    const kind = m[2].toLowerCase().startsWith('follow') ? 'followers' : 'public'
    for (let i = 0; i < Number(m[1]); i++) out.push(kind)
  }
  return out.length ? out : null
}

/** "03-09-2026 00:00" ⇄ ISO, het formaat dat het oude scherm toont. */
function toLocalStamp(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ` +
         `${p(d.getHours())}:${p(d.getMinutes())}`
}
function fromLocalStamp(text: string): string | null {
  const m = text.trim().match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})$/)
  if (!m) return null
  return `${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}:00`
}

/**
 * Eén modelrij. De velden zijn direct te bewerken en slaan op bij verlaten
 * van het veld — precies zoals het oude scherm, inclusief de helpteksten
 * eronder ("Repeats. Leave empty for all-public.", "0 = with post").
 */
function ConfigRow({
  c, p, busy, blokkade, onStart, onPause, onStop, onManage, onSaved,
  creatorPauze, onResume,
}: {
  c: Config
  p: Persona
  /** Per-creator-pauze van de identiteitsbewaking (10-10); leeg als er geen is. */
  creatorPauze?: { reason: string; since: string | null }
  onResume?: () => void
  /** Reden waarom dit model niet kan starten; leeg als hij klaar is. */
  blokkade?: string
  busy: boolean
  /** Aanzetten; na Pause verder waar hij was, na Stop een nieuwe ronde. */
  onStart: () => void
  /** Uit, het rooster blijft staan. */
  onPause: () => void
  /** Uit, afbreken, en de wachtende posts van dit model weg. */
  onStop: () => void
  onManage: () => void
  onSaved: () => void
}) {
  const { run } = useActions()
  const [iv, setIv] = useState(String(c.interval_minutes))
  const [aud, setAud] = useState(audiencePattern(c.audience_order))
  const [vd, setVd] = useState(String(c.vault_delete_delay_minutes))
  const [from, setFrom] = useState(toLocalStamp(c.start_from))
  const [until, setUntil] = useState(toLocalStamp(c.auto_stop_at))
  // Verandert de tijd buiten dit veld om (Start at +24h, Schedule All…), dan
  // hoort het veld mee te veranderen; anders toont het de oude datum.
  useEffect(() => { setFrom(toLocalStamp(c.start_from)) }, [c.start_from])
  useEffect(() => { setUntil(toLocalStamp(c.auto_stop_at)) }, [c.auto_stop_at])

  /** Staat de ingevulde einddatum te dicht bij nu? Zelfde grens als de server. */
  const untilTeVroeg = (() => {
    const iso = fromLocalStamp(until)
    if (!iso) return false
    const t = new Date(iso).getTime()
    // 07-10 (Jordy): de 24 uur aanloop is een FanCentro-regel, niet voor F2F.
    if (!p.fancentro_model_id) return false
    return Number.isFinite(t) && t < Date.now() + 24 * 3600 * 1000
  })()

  /** Wat er in de wachtrij klaarstaat. Ouder dashboard toonde dit alleen in de
   *  Manage-dialoog; met 26 modellen betekende dat 26 keer openen om te zien
   *  of een model wel gevuld is. */
  const geladen = {
    texts: c.queue_text_count ?? 0,
    media: (c.queue_photo_count ?? 0) + (c.queue_video_count ?? 0),
    get scheef() { return this.texts !== this.media },
  }

  const save = (body: Record<string, unknown>, label: string) =>
    run({
      label,
      request: mut.patch(`/api/admin/posting/config/${c.model_persona_id}`, body),
      onDone: onSaved,
      successText: 'Saved.',
    })

  return (
    <tr>
      <td>
        <b><Avatar name={p.stage_name || p.slug} src={p.avatar_url} />{p.stage_name || p.slug}</b>
        <div className="mut2" style={{ fontSize: 12, marginLeft: 34 }}>{p.slug}</div>
        {creatorPauze ? (
          <div style={{ fontSize: 11, marginLeft: 34, color: 'var(--bad)' }}
               title={creatorPauze.reason}>
            Paused by identity check{creatorPauze.since ? ` since ${creatorPauze.since.slice(0, 10)}` : ''}
            {onResume ? (
              <button className="btn ghost sm" type="button" style={{ marginLeft: 6 }}
                      onClick={onResume}>Lift pause</button>
            ) : null}
          </div>
        ) : null}
      </td>
      <td><Plat value={p.fancentro_model_id ? 'fc' : 'f2f'} /></td>
      <td className="num">
        <input className="inp cell" type="number" min={1} value={iv}
               onChange={(e) => setIv(e.target.value)}
               onBlur={() => Number(iv) !== c.interval_minutes && Number(iv) > 0
                 && save({ interval_minutes: Number(iv) }, 'Interval')} />
      </td>
      <td>
        <AudienceKeuze waarde={aud} onKies={(v) => {
                         setAud(v)
                         save({ audience_order: parsePattern(v) }, 'Audience')
                       }} />
        <span className="hint">Repeats for every cycle.</span>
      </td>
      <td className="num">
        <input className="inp cell" type="number" min={0} value={vd}
               onChange={(e) => setVd(e.target.value)}
               onBlur={() => Number(vd) !== c.vault_delete_delay_minutes
                 && save({ vault_delete_delay_minutes: Number(vd) }, 'Vault delay')} />
        <span className="hint">0 = with post</span>
      </td>
      <td>
        <input className="inp cell wide" placeholder="DD-MM-YYYY hh:mm" value={from}
               onChange={(e) => setFrom(e.target.value)}
               onBlur={() => { const v = fromLocalStamp(from)
                 if (v && v !== c.start_from) save({ start_from: v }, 'Planning starts at') }} />
        {/* 25-09 (Jordy): de oude naam "Start at" deed denken dat de bot pas
            vanaf dit moment post. Hij bepaalt alleen waar nieuwe planning
            begint; wat al gepland staat gaat gewoon door (scheduler.py pakt
            op scheduled_at, niet op start_from). */}
        <span className="hint">New posts are planned from here; posts already planned still go out.</span>
      </td>
      <td>
        {/* Post until moet minstens 24 uur vooruit staan: FanCentro publiceert
            een geplande post niet met minder aanloop. De server weigert het ook
            (422), maar dat pas na een rondje heen en weer — hier ziet Jordy het
            meteen. De grens staat in src/services/posting/post_until.py. */}
        <input className={untilTeVroeg ? 'inp cell wide scheef' : 'inp cell wide'}
               placeholder="DD-MM-YYYY hh:mm" value={until}
               title={untilTeVroeg
                 ? 'Post until must be at least 24 hours ahead — FanCentro will '
                   + 'not publish a scheduled post with less lead time'
                 : undefined}
               onChange={(e) => setUntil(e.target.value)}
               onBlur={() => { const v = fromLocalStamp(until)
                 if (v && v !== c.auto_stop_at) save({ auto_stop_at: v }, 'Post until') }} />
        {untilTeVroeg ? (
          <div className="mut2 scheef" style={{ fontSize: 11, marginTop: 2 }}>
            needs 24h lead ⚠
          </div>
        ) : null}
      </td>
      <td>
        <label className="chkrow">
          <span className={c.repeat_mode ? 'chk on' : 'chk'}
                onClick={() => save({ repeat_mode: !c.repeat_mode }, 'Repeat')}>
            {c.repeat_mode ? '✓' : ''}
          </span>
        </label>
      </td>
      <td style={{ minWidth: 130 }}>
        <div className="mut2" style={{ fontSize: 12 }}>
          {num(c.content_remaining_count)} on schedule
        </div>
        {/* Sep 7 (Jordy): "ik wil in het overzicht zien of er wel foto's en
            teksten ingeladen zijn, zodat ik niet elk model apart hoef te
            openen." Loopt het aantal teksten niet gelijk met het aantal
            media, dan valt dat hier meteen op — een foto zonder tekst gaat
            zonder onderschrift weg, een tekst zonder foto wordt nooit gepost. */}
        {/* Elk model draait zijn eigen wachtrij rond tot zijn post until; een
            model met 76 items begint in ronde 77 weer bij 1 terwijl anderen bij
            77 zitten. Dat getal hoort zichtbaar te zijn, anders lijkt het alsof
            er iets overgeslagen wordt. */}
        {c.cycle_label ? (
          <div className="mut2" style={{ fontSize: 12 }}>{c.cycle_label}</div>
        ) : null}
        <div className={geladen.scheef ? 'mut2 scheef' : 'mut2'}
             style={{ fontSize: 12 }}
             title={geladen.scheef
               ? 'Texts and media do not match — open Manage to check'
               : 'Content loaded into the queue'}>
          {num(geladen.media)} media · {num(geladen.texts)} texts
          {geladen.scheef ? ' ⚠' : ''}
        </div>
        <div className="between" style={{ fontSize: 12 }}>
          {/* 10-09 (Jordy): deze teller en die in de balk bovenin heetten
              allebei "live" en telden iets anders. Deze komt uit post_history
              en telt ALLES wat dit model ooit heeft gepost sinds zijn eerste
              wachtrij-item — dus ook wat de oude server deed. De balk bovenin
              telt alleen de lopende opdracht, van Start at tot Post until. */}
          {/* 25-09 (Jordy): het getal hierboven heette "posted ever" maar
              telde alleen de lopende ronde (post_history vanaf het eerste
              ingeplande item), terwijl de tooltip "alles ooit" beloofde. Nu
              staan ze apart: het echte totaal, en de ronde als "this run". */}
          <span className="mut" title={
            `posted ever: every post in the post history of this model`
            + (c.posted_ever_since ? ` since ${klok(c.posted_ever_since)}` : '')
            + `.\nthis run: posts since the first planned item of the current`
            + ` run, out of the items in the queue.\ntoday: since midnight`
            + ` (Amsterdam).`}>
            {typeof c.posted_ever_count === 'number'
              ? `${num(c.posted_ever_count)} posted ever · ` : ''}
            {num(c.successful_post_count)}/{num(c.total_posts)} this run
            {typeof c.posts_today === 'number'
              ? ` · ${num(c.posts_today)} today` : ''}
          </span>
          {c.active
            ? <Tag kind="ok"><span className="dotst on-live" />live</Tag>
            : <Tag kind="mut">idle</Tag>}
        </div>
        <div className="progress">
          <div className="p" style={{
            width: `${Math.min(100, Math.round(
              (c.successful_post_count / (c.total_posts || 1)) * 100))}%`,
          }} />
        </div>
        {/* 25-09 (Jordy): een model dat aan staat en niets doet, zegt waarom.
            De server leidt de reden af uit dezelfde voorwaarden als de
            planner; zie src/services/posting/waarom_stil.py. */}
        {c.active && c.idle_reason ? (
          <div className="mut2" style={{ fontSize: 12, marginTop: 3, color: 'var(--warn)' }}>
            not posting right now: {c.idle_reason}
          </div>
        ) : null}
      </td>
      <td>
        <div className="row">
          {/* Een model dat niet kan starten krijgt een grijze knop met de reden
              in de tooltip — klikken en niets zien gebeuren is erger dan een
              knop die zegt waarom hij uit staat. */}
          {c.active ? (
            <button className="btn sm" type="button" disabled={busy}
                    onClick={onPause}>Pause</button>
          ) : (
            <button className="btn primary sm" type="button"
                    disabled={busy || !!blokkade}
                    title={blokkade || undefined}
                    onClick={onStart}>Start</button>
          )}
          <button className="btn danger sm" type="button" disabled={busy}
                  onClick={onStop}>Stop</button>
          <button className="btn ghost sm" type="button" onClick={onManage}>Manage</button>
        </div>
      </td>
    </tr>
  )
}


/**
 * Hoeveel foto's er nog uit een vault moeten, en hoeveel daarvan te laat zijn.
 *
 * 10-09 (Jordy): "de reden dat dit sinds 9 augustus stilstond, is dat niemand
 * het kon zien." Een post op FanCentro verdwijnt daar vanzelf op zijn
 * vervaldatum, maar de FOTO blijft in de vault tot wij hem weghalen. Dat is
 * werk dat niemand mist tot de vault vol staat.
 *
 * Hij verschijnt alleen bij een achterstand. Wat nog niet verlopen is hoort
 * geen waarschuwing te zijn — dat is gewoon werk dat nog moet komen.
 */
type RefusedRow = {
  post_id: number; model_name: string; vault_item_id: string
  reason: string; refusals: number; next_attempt_at: string | null
  waiting: boolean
  other_posts: { fc_post_id: string; status: string; title?: string; media_ids?: string[] }[]
}

type TrendDeel = { net: number; removed: number | null; added: number | null }

type VaultBacklog = {
  fc_pending: number; fc_overdue: number
  f2f_pending: number; f2f_overdue: number
  oldest_overdue: string | null
  fc?: {
    pending: number; overdue: number; cleanable: number
    shared_number: number; shared_numbers: number; shared_reason: string
    refused: number; refused_rows: RefusedRow[]
    wasted_requests: { sinds: string; scans: number; verzoeken: number } | null
    oldest_overdue: string | null
  }
  f2f?: {
    pending: number; overdue: number; parked: boolean
    parked_until: string | null; parked_reason: string
    oldest_overdue: string | null
  }
  trend_24h?: {
    available: boolean; reason?: string; since?: string
    window_hours?: number; full_window?: boolean
    fc?: TrendDeel; f2f?: TrendDeel
  }
}

/** Waarom FanCentro een opruiming weigerde, in woorden die Jordy leest. De
 *  code staat er tussen haakjes achter, zodat hij terug te vinden is in de log. */
const WEIGERREDEN: Record<string, string> = {
  vault_item_not_exclusive_to_exact_post: 'another FanCentro post uses the same photo',
  vault_item_referenced_by_other_post: 'another FanCentro post uses the same photo',
  shared_vault_item_not_in_this_vault: "this shared number is not in this model's vault",
  shared_vault_item_used_outside_group: 'a post outside the group uses this photo',
  shared_vault_item_not_on_this_post: 'this post carries a different photo on FanCentro',
  reference_scan_incomplete: "FanCentro's post list could not be read completely",
  vault_item_still_present_after_unlink: 'FanCentro accepted the delete but kept the photo',
  exact_post_not_current: 'the post is no longer live on FanCentro',
}

function tijdje(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 16)
    : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function trendRegel(t: VaultBacklog['trend_24h'], deel: 'fc' | 'f2f'): string | null {
  if (!t) return null
  if (!t.available) return `24 h trend: ${t.reason ?? 'not available yet'}`
  const d = t[deel]
  if (!d) return null
  const venster = t.full_window ? 'Last 24 h' : `Since ${tijdje(t.since)} (${t.window_hours} h)`
  const netto = `${d.net > 0 ? '+' : ''}${nl(d.net)} net`
  if (d.added === null || d.removed === null) return `${venster}: ${netto} · cleaned count unknown`
  return `${venster}: +${nl(d.added)} new · −${nl(d.removed)} cleaned · ${netto}`
}

/**
 * De vault-achterstand, per platform en met de reden van wat blijft liggen.
 *
 * 14-09 (Jordy): FanCentro en F2F apart, niet opgeteld. Bij FanCentro wat
 * opgeruimd kan worden, wat de veiligheidsregel bewust laat staan, en wat
 * FanCentro weigert, met de reden. Bij F2F waarom het geparkeerd staat. En per
 * platform de trend over 24 uur, want één getal zegt niet of je wint.
 */
function VaultAchterstand({ b }: { b: VaultBacklog }) {
  const fc = b.fc
  const f2f = b.f2f
  if (!fc || !f2f) {
    // Een oudere backend zonder de uitsplitsing: het oude beeld.
    const teLaat = b.fc_overdue + b.f2f_overdue
    if (!teLaat) return null
    return (
      <div className="mut2" style={{ fontSize: 12, marginBottom: 12 }}>
        {`FanCentro ${nl(b.fc_overdue)} overdue of ${nl(b.fc_pending)} waiting`}
        {` · F2F ${nl(b.f2f_overdue)} overdue of ${nl(b.f2f_pending)} waiting`}
      </div>
    )
  }
  if (!fc.overdue && !f2f.overdue) return null
  const kader = (kleur: string) => ({
    border: `1px solid ${kleur}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12,
  })
  const parkeerDatum = f2f.parked_until
    ? new Date(f2f.parked_until).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : ''
  const fcTrend = trendRegel(b.trend_24h, 'fc')
  const f2fTrend = trendRegel(b.trend_24h, 'f2f')
  return (
    <>
      {fc.overdue ? (
        <div style={{ ...kader('var(--warn)'), background: 'rgba(255,180,60,.10)' }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--warn)' }}>
            {`FanCentro: ${nl(fc.overdue)} photos are still in a vault after their post expired`}
          </div>
          <div className="mut2" style={{ fontSize: 12, marginTop: 3 }}>
            {`${nl(fc.cleanable)} can be cleaned`}
            {` · ${nl(fc.refused)} refused by FanCentro, set aside`}
            {` · ${nl(fc.shared_number)} on ${nl(fc.shared_numbers)} shared vault numbers, kept on purpose`}
            {fc.oldest_overdue ? ` · oldest since ${fc.oldest_overdue.slice(0, 16)}` : ''}
          </div>
          {fcTrend ? (
            <div style={{ fontSize: 12, marginTop: 3 }}>{fcTrend}</div>
          ) : null}
          {fc.shared_number ? (
            <div className="mut2" style={{ fontSize: 11, marginTop: 4 }}>
              {`Shared vault numbers: ${fc.shared_reason}.`}
            </div>
          ) : null}
          {fc.refused_rows.length ? (
            <div style={{ fontSize: 11, marginTop: 6 }}>
              {fc.refused_rows.slice(0, 10).map((r) => (
                <div key={r.post_id} className="mut2">
                  {`Post ${r.post_id} · ${r.model_name} · ${WEIGERREDEN[r.reason] ?? r.reason}`}
                  {` (${r.reason}) · refused ${nl(r.refusals)}×`}
                  {r.waiting && r.next_attempt_at ? ` · next try ${tijdje(r.next_attempt_at)}` : ' · due for a retry'}
                  {r.other_posts.length
                    ? ` · also used by ${r.other_posts.map((o) => `post ${o.fc_post_id}${o.status ? ` (${o.status})` : ''}`).join(', ')}`
                    : ''}
                </div>
              ))}
            </div>
          ) : null}
          {fc.wasted_requests ? (
            <div className="mut2" style={{ fontSize: 11, marginTop: 4 }}>
              {`Cleanup scans that removed nothing cost FanCentro ${nl(fc.wasted_requests.verzoeken)} requests `}
              {`in ${nl(fc.wasted_requests.scans)} scans since ${tijdje(fc.wasted_requests.sinds)}.`}
            </div>
          ) : null}
        </div>
      ) : null}
      {f2f.overdue ? (
        <div style={kader('var(--line, rgba(255,255,255,.15))')}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {`F2F: ${nl(f2f.overdue)} photos past their post's expiry, of ${nl(f2f.pending)} waiting`}
          </div>
          <div className="mut2" style={{ fontSize: 12, marginTop: 3 }}>
            {f2f.parked
              ? `Parked until ${parkeerDatum} — ${f2f.parked_reason}. Nothing is cleaned before then.`
              : 'Cleaned by the F2F cleanup.'}
            {f2f.oldest_overdue ? ` · oldest since ${f2f.oldest_overdue.slice(0, 16)}` : ''}
          </div>
          {f2fTrend && !f2f.parked ? (
            <div style={{ fontSize: 12, marginTop: 3 }}>{f2fTrend}</div>
          ) : null}
        </div>
      ) : null}
    </>
  )
}


/**
 * De twee posting-poorten, bovenaan en niet weg te klikken.
 *
 * Hij verschijnt alleen als er iets dicht staat of onleesbaar is. Staat alles
 * open, dan hoort er geen balk te zijn — een waarschuwing die er altijd staat
 * leest niemand meer.
 */
function PoortMelding({ gates }: {
  gates: Record<'publish' | 'cleanup', {
    open: boolean; mode: string; describes: string; unset: boolean
    env: string; error: string
  }>
}) {
  const regels: { kop: string; uitleg: string; env: string; erg: boolean }[] = []
  const p = gates.publish
  const c = gates.cleanup
  if (p && p.error) {
    regels.push({ kop: 'Publishing gate unreadable', uitleg: p.error,
                  env: p.env, erg: true })
  } else if (p && !p.open) {
    regels.push({
      kop: 'Publishing gate closed — no posts will be handed over',
      uitleg: p.unset
        ? 'Nothing is configured, so nothing publishes. Models you start will '
          + 'pick up work and every post is stopped at the gate.'
        : 'Set to none. Models you start will pick up work and every post is '
          + 'stopped at the gate.',
      env: p.env, erg: true,
    })
  }
  if (c && c.error) {
    regels.push({ kop: 'Cleanup gate unreadable', uitleg: c.error,
                  env: c.env, erg: true })
  } else if (c && !c.open) {
    regels.push({
      kop: 'Cleanup gate closed — nothing is expired or removed from the vault',
      uitleg: c.unset
        ? 'Nothing is configured. Posts keep their vault item after they '
          + 'expire on the platform.'
        : 'Set to none. Posts keep their vault item after they expire on the '
          + 'platform.',
      env: c.env, erg: false,
    })
  }
  if (!regels.length) return null
  return (
    <div style={{ marginBottom: 12 }}>
      {regels.map((r) => (
        <div
          key={r.env}
          style={{
            border: `1px solid ${r.erg ? 'var(--bad)' : 'var(--warn)'}`,
            background: r.erg ? 'rgba(255,80,80,.10)' : 'rgba(255,180,60,.10)',
            borderRadius: 10, padding: '10px 12px', marginBottom: 8,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13,
                        color: r.erg ? 'var(--bad)' : 'var(--warn)' }}>
            {r.kop}
          </div>
          <div className="mut2" style={{ fontSize: 12, marginTop: 3 }}>
            {r.uitleg}
          </div>
          <div className="mut2" style={{ fontSize: 11, marginTop: 4 }}>
            {`Set ${r.env} in the environment file and restart the worker.`}
          </div>
        </div>
      ))}
    </div>
  )
}


/** "in 2u 15m" / "over tijd" — de ETA-kolom van de oude wachtrij. */
function etaFrom(when: string): string {
  const t = new Date(when).getTime()
  if (!Number.isFinite(t)) return '—'
  const s = (t - Date.now()) / 1000
  if (s <= 0) return 'due'
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d > 0) return `in ${d}d ${h}h`
  if (h > 0) return `in ${h}h ${m}m`
  return `in ${m}m`
}
